import { api } from '../lib/api';

export interface QualityOverviewSummary {
  docsCount: number;
  hazardsCount: number;
}

export interface QualityDocItem {
  id: number;
  docType: string;
  chapter: string | null;
  title: string;
  intro: string | null;
  description: string | null;
  legislation: string | null;
  recommendations: string | null;
  hazardSummary: string | null;
  category: string | null;
  fileUrl: string | null;
  updatedAt: string | null;
}

export interface QualityDocUpdatePayload {
  chapter: string;
  title: string;
  intro: string;
  description: string;
  legislation: string;
  recommendations: string;
  hazardSummary: string;
  category: string;
}

export interface QualityVisitorItem {
  id: number;
  entryDate: string;
  visitorName: string;
  purpose: string;
  ackHygiene: boolean;
  ackHair: boolean;
  ackNoJewelry: boolean;
  createdAt: string;
  createdByName: string | null;
}

export interface QualityHazardItem {
  id: number;
  chapter: string;
  processStep: string;
  hazard: string;
  action: string;
  docTitle: string | null;
  updatedAt: string | null;
}

export interface QualityHazardPayload {
  chapter: string;
  processStep: string;
  hazard: string;
  action: string;
}

export type QualitySelfcheckType =
  | 'ccp'
  | 'cleaning'
  | 'calibration_scale'
  | 'calibration_refractometer'
  | 'pest_control'
  | 'templog';

export interface QualitySelfcheckEntry {
  id: number;
  checklistType: 'cleaning' | 'calibration_scale' | 'calibration_refractometer' | 'pest_control';
  entryDate: string;
  comment: string | null;
  userId: number | null;
  userName: string | null;
  createdAt: string;
}

export interface QualityCcpItem {
  containerId: number;
  containerCode: string | null;
  hinkLabel: string;
  qtyKg: number;
  waterPercent: number;
  extractionDate: string;
  extractionCode: string;
  apiaryName: string | null;
  status: 'OK' | 'HIGH';
  deviationId: number | null;
  deviationStatus: string | null;
}

export interface QualityCcpSummary {
  measuredCount: number;
  overCount: number;
  averageWaterPercent: number;
  maxWaterPercent: number;
}

export const qualityApi = {
  overview: () => api.get<{ summary: QualityOverviewSummary }>('/office/quality/overview'),
  docs: () =>
    api.get<{ items: QualityDocItem[] }>('/office/quality/docs'),
  doc: (id: number | string) =>
    api.get<{ item: QualityDocItem }>(`/office/quality/docs/${encodeURIComponent(String(id))}`),
  updateDoc: (id: number | string, payload: QualityDocUpdatePayload) =>
    api.patch<{ item: QualityDocItem }>(`/office/quality/docs/${encodeURIComponent(String(id))}`, payload),
  visitors: (limit = 500) =>
    api.get<{ items: QualityVisitorItem[] }>(`/office/quality/visitors?limit=${limit}`),
  createVisitor: (payload: {
    entryDate: string;
    visitorName: string;
    purpose: string;
    ackHygiene: boolean;
    ackHair: boolean;
    ackNoJewelry: boolean;
  }) => api.post<{ ok: boolean; id: number }>('/office/quality/visitors', payload),
  hazards: (chapter = '') =>
    api.get<{ items: QualityHazardItem[] }>(
      chapter.trim() ? `/office/quality/hazards?chapter=${encodeURIComponent(chapter.trim())}` : '/office/quality/hazards',
    ),
  createHazard: (payload: QualityHazardPayload) =>
    api.post<{ ok: boolean; id: number }>('/office/quality/hazards', payload),
  updateHazard: (id: number, payload: QualityHazardPayload) =>
    api.patch<{ ok: boolean }>(`/office/quality/hazards/${id}`, payload),
  deleteHazard: (id: number) =>
    api.delete<{ ok: boolean }>(`/office/quality/hazards/${id}`),
  ccp: (onlyOver = false) =>
    api.get<{ summary: QualityCcpSummary; items: QualityCcpItem[] }>(`/office/quality/selfchecks/ccp?onlyOver=${onlyOver ? 1 : 0}`),
  selfcheckEntries: (type: Exclude<QualitySelfcheckType, 'ccp' | 'templog'>) =>
    api.get<{ items: QualitySelfcheckEntry[] }>(`/office/quality/selfchecks/entries?type=${encodeURIComponent(type)}`),
  createSelfcheckEntry: (payload: {
    type: Exclude<QualitySelfcheckType, 'ccp' | 'templog'>;
    entryDate: string;
    comment: string;
    userName?: string;
  }) => api.post<{ ok: boolean; id: number }>('/office/quality/selfchecks/entries', payload),
  deleteSelfcheckEntry: (id: number) =>
    api.delete<{ ok: boolean }>(`/office/quality/selfchecks/entries/${id}`),
  createDeviation: (containerId: number) =>
    api.post<{ ok: boolean; id: number }>('/office/quality/selfchecks/deviations', { containerId }),
};
